/**
 * Shared hook for the disconnect-with-data-deletion flow.
 *
 * Both Gmail and Outlook use the same two-dialog pattern:
 *   1. Disconnect confirmation dialog — shown when clicking "Disconnect" on a
 *      connected account. Includes a checklist to optionally delete synced data.
 *   2. Delete synced data dialog — shown when clicking "Delete synced data" on
 *      an already-disconnected (orphaned) account.
 *
 * This hook encapsulates all the state, handlers, and the dynamic description
 * for both dialogs. Provider-specific behaviour is injected via the options.
 */

import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import type { DeleteSyncedDataChecklistRow } from '@/components/integrations/DeleteSyncedDataChecklist';

import { useDeleteIntegrationSyncedData } from './google/useDeleteIntegrationSyncedData';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Minimum shape an account object must have to work with this hook. */
interface AccountWithEmail {
  email: string | null;
}

interface DeleteTarget {
  email: string;
  rows: DeleteSyncedDataChecklistRow[];
}

interface UseDisconnectWithDeletionOptions<TAccount extends AccountWithEmail> {
  orgId: string | undefined;

  /** Extract the checklist rows from a provider-specific account object. */
  buildChecklistRows: (account: TAccount) => DeleteSyncedDataChecklistRow[];

  /**
   * Perform the provider-specific disconnect API call.
   * Must throw on failure so the hook can abort the flow.
   */
  disconnect: (account: TAccount) => Promise<void>;

  /**
   * Base sentence shown in the disconnect confirmation dialog when no
   * deletion checkboxes are checked.
   */
  baseDescription: string;

  /** Provider label for the success toast (e.g. "Account", "Outlook"). */
  toastLabel: string;
}

// ---------------------------------------------------------------------------
// Shared deletion-error toast
// ---------------------------------------------------------------------------

function showDeletionErrorToast(
  row: DeleteSyncedDataChecklistRow,
  context: 'disconnect' | 'standalone'
) {
  const what =
    row.entityType === 'ahoy_email_thread' ? 'emails' : 'calendar events';
  const description =
    context === 'disconnect'
      ? `Your account was disconnected, but we couldn't start the ${what} deletion. Try again from the integration settings.`
      : `Could not start the ${what} deletion. Try again from the integration settings.`;
  toast.error("We couldn't start the deletion", { description });
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useDisconnectWithDeletion<TAccount extends AccountWithEmail>({
  orgId,
  buildChecklistRows,
  disconnect,
  baseDescription,
  toastLabel,
}: UseDisconnectWithDeletionOptions<TAccount>) {
  const { mutateAsync: deleteSyncedDataAsync } =
    useDeleteIntegrationSyncedData();

  // ------ Disconnect confirmation dialog state ------

  const [disconnectEmail, setDisconnectEmail] = useState<string | null>(null);
  const [pendingAccount, setPendingAccount] = useState<TAccount | null>(null);
  const [checklistRows, setChecklistRows] = useState<
    DeleteSyncedDataChecklistRow[]
  >([]);

  // ------ Delete synced data dialog state (orphaned cards) ------

  const [disconnectedCardDeleteTarget, setDisconnectedCardDeleteTarget] =
    useState<DeleteTarget | null>(null);

  // ------ Disconnect confirmation handlers ------

  const handleDisconnectClick = useCallback(
    (account: TAccount) => {
      setPendingAccount(account);
      setDisconnectEmail(account.email ?? '');
      setChecklistRows(buildChecklistRows(account));
    },
    [buildChecklistRows]
  );

  const handleChecklistToggle = useCallback((entityType: string) => {
    setChecklistRows(prev =>
      prev.map(r =>
        r.entityType === entityType ? { ...r, isChecked: !r.isChecked } : r
      )
    );
  }, []);

  const handleConfirmDisconnect = useCallback(async () => {
    if (pendingAccount === null || disconnectEmail === null) {
      return;
    }
    const email = disconnectEmail;
    const checkedRows = checklistRows.filter(r => r.isChecked);

    try {
      await disconnect(pendingAccount);
    } catch (err) {
      console.error('Disconnect failed:', err);
      setDisconnectEmail(null);
      setPendingAccount(null);
      setChecklistRows([]);
      toast.error("We couldn't disconnect", { description: 'Try again.' });
      return;
    }

    setDisconnectEmail(null);
    setPendingAccount(null);
    setChecklistRows([]);
    toast.success(`${toastLabel} disconnected`, {
      description: `${email} has been disconnected.`,
    });

    if (checkedRows.length > 0 && orgId) {
      const results = await Promise.allSettled(
        checkedRows.map(r =>
          deleteSyncedDataAsync({
            orgId,
            integrationEntityId: r.integrationEntityId,
            entityTypes: [r.entityType],
          })
        )
      );
      results.forEach((result, idx) => {
        if (result.status === 'rejected') {
          console.error(
            'Failed to delete synced data for',
            checkedRows[idx].entityType,
            result.reason
          );
          showDeletionErrorToast(checkedRows[idx], 'disconnect');
        }
      });
    }
  }, [
    pendingAccount,
    disconnectEmail,
    checklistRows,
    disconnect,
    toastLabel,
    orgId,
    deleteSyncedDataAsync,
  ]);

  const handleCancelDisconnect = useCallback(() => {
    setDisconnectEmail(null);
    setPendingAccount(null);
    setChecklistRows([]);
  }, []);

  // ------ Delete synced data dialog handlers (orphaned cards) ------

  const handleOpenDeleteSyncedData = useCallback(
    (account: TAccount) => {
      setDisconnectedCardDeleteTarget({
        email: account.email ?? '',
        rows: buildChecklistRows(account),
      });
    },
    [buildChecklistRows]
  );

  const handleDeleteRowToggle = useCallback((entityType: string) => {
    setDisconnectedCardDeleteTarget(prev => {
      if (!prev) {
        return null;
      }
      return {
        ...prev,
        rows: prev.rows.map(r =>
          r.entityType === entityType ? { ...r, isChecked: !r.isChecked } : r
        ),
      };
    });
  }, []);

  const handleCancelDeleteSyncedData = useCallback(() => {
    setDisconnectedCardDeleteTarget(null);
  }, []);

  const handleConfirmDeleteSyncedData = useCallback(async () => {
    if (!disconnectedCardDeleteTarget || !orgId) {
      return;
    }
    const { email, rows } = disconnectedCardDeleteTarget;
    const checkedRows = rows.filter(r => r.isChecked);
    setDisconnectedCardDeleteTarget(null);
    const results = await Promise.allSettled(
      checkedRows.map(r =>
        deleteSyncedDataAsync({
          orgId,
          integrationEntityId: r.integrationEntityId,
          entityTypes: [r.entityType],
        })
      )
    );
    const succeededRows = checkedRows.filter(
      (_, idx) => results[idx].status === 'fulfilled'
    );
    results.forEach((result, idx) => {
      if (result.status === 'rejected') {
        console.error(
          'Failed to delete synced data for',
          checkedRows[idx].entityType,
          result.reason
        );
        showDeletionErrorToast(checkedRows[idx], 'standalone');
      }
    });
    if (succeededRows.length > 0) {
      let what: string;
      if (succeededRows.length === 2) {
        what = 'synced emails and calendar events';
      } else if (succeededRows[0].entityType === 'ahoy_email_thread') {
        what = 'synced emails';
      } else {
        what = 'synced calendar events';
      }
      toast.success('Deletion started', {
        description: `We're deleting the ${what} for ${email}. This may take a few minutes.`,
      });
    }
  }, [disconnectedCardDeleteTarget, deleteSyncedDataAsync, orgId]);

  // ------ Dynamic description ------

  const checkedDescription = useMemo(() => {
    const checked = checklistRows.filter(r => r.isChecked);
    if (checked.length === 0) {
      return baseDescription;
    }
    let what: string;
    if (checked.length === 2) {
      what = 'synced emails and calendar events';
    } else if (checked[0].entityType === 'ahoy_email_thread') {
      what = 'synced emails';
    } else {
      what = 'synced calendar events';
    }
    return `${baseDescription} This will also permanently delete all ${what} for this account. This cannot be undone. Your contacts and deals will not be affected.`;
  }, [checklistRows, baseDescription]);

  return {
    // Disconnect dialog
    isDisconnectDialogOpen: disconnectEmail !== null,
    checklistRows,
    checkedDescription,
    handleDisconnectClick,
    handleChecklistToggle,
    handleConfirmDisconnect,
    handleCancelDisconnect,

    // Delete synced data dialog
    disconnectedCardDeleteTarget,
    handleOpenDeleteSyncedData,
    handleDeleteRowToggle,
    handleConfirmDeleteSyncedData,
    handleCancelDeleteSyncedData,
  };
}
